import { QueryObserverResult } from '@tanstack/react-query';

import { clear as clearLocalStorage } from '@rover/utilities/localStorage';
import { clear as clearSessionStorage } from '@rover/utilities/sessionStorage';

import { useApiCurrentUserRetrieve } from '../../../apiClient/latest/api-current-user-retrieve';
import { useApiFrontendUserPersonSummaryRetrieve } from '../../../apiClient/latest/api-frontend-user-person-summary-retrieve';
import type {
  CurrentUser,
  CurrentUserViewSchema,
  Me,
  Pet,
  UserInformation,
  UserSessionInformationUtmParameters,
} from '../../../apiClient/latest/model';
import { ErrorType } from '../../Network/orvalHTTPInterface';

import { UserModeEnum } from './UserModeEnum';

export type LogoutConfig = {
  nextLocation: string;
  ref?: string;
  beforeNavCallback?: () => void;
};

/** parses a UserModeEnum from the passed CurrentUserViewSchema */
export const getUserMode = (user?: CurrentUserViewSchema): UserModeEnum => {
  if (!user || !user.authenticated) return UserModeEnum.UNAUTHED;
  const isSitter = (user as CurrentUser).currentUser.userMode === 'provider';
  return isSitter ? UserModeEnum.SITTER : UserModeEnum.OWNER;
};

/** attempts to return a specific pet from the provided `Me` model data */
export const getUserSummaryPetByOpk = (opk: string, data?: Me): Pet | undefined => {
  if (!data) return undefined;
  const { pets, memorializedPets } = data;
  return [...pets, ...memorializedPets].find((pet) => pet.opk === opk);
};

export type UseCurrentUser = ReturnType<typeof useApiCurrentUserRetrieve> & {
  data?: CurrentUserViewSchema;
  refetch: () => Promise<QueryObserverResult<CurrentUserViewSchema, ErrorType<Error>>>;
  error: ErrorType<Error> | null;
  firstHttpReferrer?: string | null;
  isActiveServiceProvider?: boolean;
  isAnApprovedProvider?: boolean;
  isAuthenticated?: boolean;
  isIdentityVerificationRequired?: boolean;
  isImpersonating?: boolean;
  logout?: (config: LogoutConfig) => void;
  peopleUrl?: string;
  user?: UserInformation;
  userMode: UserModeEnum;
  utmParams?: UserSessionInformationUtmParameters;
};

export const logout = ({ nextLocation, ref, beforeNavCallback }: LogoutConfig): void => {
  clearLocalStorage();
  clearSessionStorage();
  beforeNavCallback && beforeNavCallback();
  const encodedLocation = encodeURIComponent(nextLocation);

  window.location.href = `/logout/?next=${encodedLocation}${ref ? `&${ref}` : ''}`; // This is the actual logout
};

/**
 * Provides a react-query interface to the CurrentUser model,
 * plus some shortcuts to commonly used data
 */
export const useCurrentUser = ({
  enabled = true,
  refetchOnMount,
}: Partial<{
  enabled: boolean;
  refetchOnMount: boolean | 'always';
}> = {}): UseCurrentUser => {
  const query = useApiCurrentUserRetrieve({ query: { enabled, refetchOnMount } });
  const isAuthenticated = query.data?.authenticated;
  const { refetch } = query;
  if (query.isLoading)
    return { ...query, refetch, isAuthenticated, userMode: UserModeEnum.UNAUTHED };

  const userData = query.data as CurrentUser;
  const firstHttpReferrer = userData?.session?.firstHttpReferrer;
  const utmParams = userData?.session?.utmParameters;

  if (!isAuthenticated)
    return {
      ...query,
      refetch,
      firstHttpReferrer,
      isAuthenticated,
      userMode: UserModeEnum.UNAUTHED,
      utmParams,
    };

  const { currentUser: user, realUser } = userData;
  const userMode = getUserMode(userData);
  const { isActiveServiceProvider, isAnApprovedProvider, isIdentityVerificationRequired } = user;
  const isImpersonating = !!realUser?.opk.length;
  const peopleUrl = `/admin/people/person/?slug=${user.slug}`;

  return {
    ...query,
    refetch,
    firstHttpReferrer,
    isActiveServiceProvider,
    isAnApprovedProvider,
    isAuthenticated,
    isIdentityVerificationRequired,
    isImpersonating,
    logout,
    peopleUrl,
    user,
    userMode,
    utmParams,
  };
};

export type UseAuthedUserSummary = ReturnType<typeof useApiFrontendUserPersonSummaryRetrieve> & {
  calendarUrl?: string;
  data?: Me; // seems to be required to type this again?
  error: ErrorType<Error> | null;
  getPetByOpk: (opk: string) => Pet | undefined;
  hasPets: boolean;
  insightsUrl?: string;
  memorializedPets?: Pet[];
  pets?: Pet[];
  sitterProfileUrl?: string;
  webEditProfileUrl?: string;
};
/**
 * If the current user is authed, provides a react-query interface
 * to the Me model, plus some shortcuts to commonly used data
 */
export const useAuthedUserSummary = (options?: {
  query?: {
    enabled?: boolean;
  };
}): UseAuthedUserSummary => {
  const query = useApiFrontendUserPersonSummaryRetrieve(options);
  const { data } = query;
  const calendarUrl = data?.urls.calendar;
  const insightsUrl = data?.urls.insightsDashboard;
  const sitterProfileUrl = data?.urls.sitterProfile;
  const webEditProfileUrl = data?.urls.webEditProfile;
  const pets = data?.pets;
  const hasPets = Array.isArray(pets) && pets.length > 0;
  return {
    ...query,
    calendarUrl,
    getPetByOpk: (opk) => getUserSummaryPetByOpk(opk, data),
    hasPets,
    insightsUrl,
    memorializedPets: data?.memorializedPets,
    pets,
    sitterProfileUrl,
    webEditProfileUrl,
  };
};
